import React, { type SetStateAction, type Dispatch, type FC } from 'react';
import { Link } from 'react-router-dom';
import Address from '@ynap/address';
import Notification from '@ynap/notification';
import BEMHelper from '@ynap/bem-helper';
import { EditAddressAction, AddAddressAction } from './@types';
import { Field as FormField, FormValues, FieldRenderProps, useFormField } from '@ynap/form';
import { StopPropagation } from '@ynap/overlay';
import { RadioButton } from '@ynap/radio-button';
import { getFields } from '@ynap/address-form-v1/src/AddressForm/AddressFields';
import { AddressForm } from '../AddressForm';
import { Locale, Config, Messages, CheckoutAnalytics } from '../AddressForm/@types';
import { mapAddressValues, useAddressValidateContext } from '../AddressForm/utils';
import { TrackingProp } from 'react-tracking';

const bem = new BEMHelper('AddressSelectionForm');

type AnyFn = (...args: any[]) => any;

export interface AddressRadioButtonProps {
    config: Config;
    locale: Locale;
    messages: Messages;
    address: FormValues;
    match: {
        path: string;
        url: string;
    };
    onChange: AnyFn;
    isRestricted?: boolean;
    editAddress?: EditAddressAction;
    addAddress?: AddAddressAction;
    trackEditAddress?: AnyFn;
    defaultAddress?: string;
    isCheckoutLayout?: boolean;
    trackUpdateAddress?: AnyFn;
    setEditAddressID?: AnyFn;
    onSubmit?: (values: FormValues) => any;
    onCancel?: () => void;
    editAddressID?: number | string;
    editable?: boolean;
    setSaveEditing?: AnyFn;
    saveEditing?: number;
    hideCountry?: boolean;
    tracking?: TrackingProp;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    split?: boolean;
    isGuestUser?: boolean;
    reduxForm?: any;
    removeAddressFinder?: AnyFn;
    setRemoveAddressFinder?: Dispatch<SetStateAction<() => any>>;
    invalidFields?: string[];
}

const AddressRadioButton: FC<AddressRadioButtonProps> = ({
    config,
    locale,
    messages,
    address,
    match,
    onChange: onChangeParent,
    isRestricted,
    editAddress,
    trackEditAddress,
    defaultAddress,
    isCheckoutLayout,
    trackUpdateAddress,
    setEditAddressID,
    onSubmit,
    onCancel,
    editAddressID,
    editable = true,
    setSaveEditing,
    saveEditing,
    hideCountry,
    tracking,
    primaryCategory,
    checkoutAnalytics,
    split,
    isGuestUser = false,
    reduxForm,
    removeAddressFinder,
    setRemoveAddressFinder,
    invalidFields,
}) => {
    const [addressIdField, setAddressIdField] = useFormField('addressId');

    const { countries } = useAddressValidateContext();
    const { head } = config;
    const enableLoqateOn = head?.enableLoqateOn?.includes(locale.country);
    const addressFinderEnabled = config.head.loqate?.enabled && enableLoqateOn;
    const fieldsWCS = getFields(countries, (address?.country as string) || locale.country, addressFinderEnabled);
    const stateField = fieldsWCS?.find((fieldWCS) => fieldWCS.wcsType?.name === 'state');

    const handleSubmit = (values: FormValues) => {
        const isSavedStateNameInvalid =
            !!address?.state &&
            !!stateField?.values?.length &&
            !stateField?.mandatory &&
            typeof stateField.values.find((thisVal) => address.state === (typeof thisVal !== 'string' ? thisVal.value : thisVal)) === 'undefined';

        const allowedEmptyValues: string[] = [];

        if (isSavedStateNameInvalid) {
            allowedEmptyValues.push('state');
        }

        if (editAddress) {
            const mappedValues = mapAddressValues(values, locale);
            trackUpdateAddress?.();

            // We are about to edit an address (we only do this if we are in checkoutV3, which here is safeguarded by the existance of setSaveEditing)
            setSaveEditing?.(1);

            return editAddress(mappedValues, allowedEmptyValues).then(({ payload: { addressId } }) => {
                // Need to update the currently selected address
                setAddressIdField({ ...addressIdField, value: addressId });
                if (isCheckoutLayout) {
                    // force cast to number since addressId can come as string from api
                    setSaveEditing?.(Number(addressId));
                    return onSubmit?.({ ...values, addressId }).finally(() => setSaveEditing?.(0)); // reset state after everything is finished
                }
            });
        }
    };

    return (
        <>
            {isCheckoutLayout && editAddressID === address.addressId && (
                <StopPropagation>
                    <AddressForm
                        loading={saveEditing ? saveEditing > 1 : false}
                        config={config}
                        country={locale.country}
                        initialValues={address}
                        onSubmit={handleSubmit}
                        onCancel={onCancel}
                        messages={messages}
                        touched={true}
                        isCheckoutLayout={isCheckoutLayout}
                        hideCountry={hideCountry}
                        tracking={tracking}
                        primaryCategory={primaryCategory}
                        checkoutAnalytics={checkoutAnalytics}
                        reduxForm={reduxForm}
                        removeAddressFinder={removeAddressFinder}
                        setRemoveAddressFinder={setRemoveAddressFinder}
                        invalidFields={invalidFields}
                    />
                </StopPropagation>
            )}
            {(!isCheckoutLayout || editAddressID !== address.addressId) && (
                <FormField name="addressId" key={String(address.addressId)} disabled={isRestricted} value={defaultAddress || ''}>
                    {({ value, onChange, ...rest }: FieldRenderProps) => {
                        const checked = address.addressId == (value == '' ? defaultAddress : value);
                        return (
                            <>
                                <RadioButton
                                    {...rest}
                                    id={address.addressId}
                                    value={address.addressId}
                                    checked={checked}
                                    className={bem(
                                        'address',
                                        { disabled: isRestricted, transient: address.transient },
                                        isCheckoutLayout ? 'isCheckoutLayout' : 'overide',
                                    )}
                                    label={<Address {...address} isCheckoutLayout={isCheckoutLayout} />}
                                    notification={
                                        address?.transient &&
                                        !split &&
                                        !isGuestUser && (
                                            <Notification
                                                className={bem('transientNotifiaction', { isCheckoutLayout })}
                                                type="info"
                                                message={messages?.shippingAddressForm?.temporaryAddress?.()}
                                            />
                                        )
                                    }
                                    description={
                                        editAddress && !isCheckoutLayout ? (
                                            <Link className={bem('link')} to={`${match.url}/edit/${address.addressId}`} onClick={() => trackEditAddress?.()}>
                                                {messages.addressSelectionForm?.edit?.()}
                                            </Link>
                                        ) : null
                                    }
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                        onChangeParent();
                                        onChange(ev.target.value);
                                        setEditAddressID?.(`${address.addressId}Selected`);
                                    }}
                                >
                                    {isCheckoutLayout && editable && !(address.transient || address.isTransient) && (
                                        <div
                                            className={bem('editPenButton')}
                                            onClick={() => {
                                                setEditAddressID?.(address.addressId);
                                                trackEditAddress?.();
                                                // fix: on edit: put radio checked
                                                address.addressId && onChange(`${address.addressId}`);
                                            }}
                                        >
                                            <div className={bem('editPen')} />
                                        </div>
                                    )}

                                    {isRestricted && (
                                        <Notification
                                            className={bem('shipping-restricted')}
                                            type={'error'}
                                            message={messages.notifications?.shippingRestricted?.() || ''}
                                        />
                                    )}
                                </RadioButton>
                            </>
                        );
                    }}
                </FormField>
            )}
        </>
    );
};

export default AddressRadioButton;
